import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUp,
  ArrowUpRight,
  ArrowRight,
  Sparkles,
  Mail,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  RefreshCw,
  X,
  LogOut,
  Send,
  MessagesSquare,
} from 'lucide-react';

import {
  Container,
  Button,
  PhoneInput,
  composePhoneNumber,
  DEFAULT_PHONE_VALUE,
  type PhoneValue,
} from '../ui';
import { cn } from '../../utils/cn';
import fluidSphere from '../../assets/dynamic_fluid_sphere.gif';
import {
  clearAiSession,
  loadStoredAiSession,
  persistAiSession,
  sendAiChatMessage,
  startAiChatSession,
  verifyAiChatSession,
  type ChatTurn,
  type StoredAiSession,
} from '../../services/aiChatApi';

// ---------------------------------------------------------------------------
// Quick picks — one-line prompts that seed the conversation.
// ---------------------------------------------------------------------------

interface QuickPick {
  title: string;
  prompt: string;
}

const QUICK_PICKS: QuickPick[] = [
  {
    title: "I'm facing a tech challenge in my current stack",
    prompt:
      "I'm facing a tech challenge in my current stack and I'm not sure where to start. Can you help me think through where Istatec would typically begin?",
  },
  {
    title: 'Reduce cloud & engineering spend',
    prompt:
      'We want to reduce our cloud + engineering spend without slowing delivery. What levers does Istatec pull for cost optimisation?',
  },
  {
    title: 'Need senior GenAI developers',
    prompt:
      'We need to hire senior GenAI developers on contract or full-time to ship an LLM product in the next quarter. How does Istatec engage?',
  },
  {
    title: 'Build an AI agent for our workflow',
    prompt:
      "We want to build an internal AI agent that automates a specific workflow (support, sales-ops, or finance). Where would we start with Istatec?",
  },
  {
    title: 'Modernise a legacy backend',
    prompt:
      'Our backend is a monolith on old infrastructure and slowing us down. How does Istatec approach modernisation without a big-bang rewrite?',
  },
  {
    title: 'Extract data from documents at scale',
    prompt:
      'We need to extract transactions and structured data from documents (PDFs, statements, invoices, contracts) at scale. Tell me how Istatec (and FinExtract) can help.',
  },
];

// ---------------------------------------------------------------------------
// Chat message shape (extends server ChatTurn with a client-side id + state).
// ---------------------------------------------------------------------------

interface ChatMessage extends ChatTurn {
  id: string;
  pending?: boolean;
  error?: boolean;
}

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

type Phase = 'idle' | 'need-email' | 'sending-otp' | 'need-otp' | 'chatting';

const OTP_RESEND_COOLDOWN = 60;

export const AskIstatecAI: React.FC = () => {
  // ─── session ───────────────────────────────────────────────────────────
  const [session, setSession] = useState<StoredAiSession | null>(null);

  // ─── input + phase ─────────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('idle');
  const [pendingUserMessage, setPendingUserMessage] = useState<string>('');

  // ─── auth modal state ──────────────────────────────────────────────────
  const [authName, setAuthName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPhone, setAuthPhone] = useState<PhoneValue>(DEFAULT_PHONE_VALUE);
  const [authError, setAuthError] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpResending, setOtpResending] = useState(false);
  const otpInputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // ─── chat state ────────────────────────────────────────────────────────
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);

  // Restore any prior verified session on mount.
  useEffect(() => {
    const restored = loadStoredAiSession();
    if (restored) {
      setSession(restored);
    }
  }, []);

  // Auto-scroll the chat panel as messages arrive.
  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [messages, isThinking]);

  // OTP resend countdown.
  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const t = setInterval(() => setOtpResendCooldown((s) => (s <= 0 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpResendCooldown]);

  const hasActiveSession = useMemo(
    () => Boolean(session && session.expires_at * 1000 > Date.now()),
    [session],
  );

  // ─── flow: try to send a message ───────────────────────────────────────
  const attemptSend = useCallback(
    (rawText: string) => {
      const text = rawText.trim();
      if (!text) return;

      if (!hasActiveSession) {
        // Buffer the message and pop the auth modal.
        setPendingUserMessage(text);
        setAuthError('');
        setOtpError('');
        setPhase('need-email');
        return;
      }

      // Session ready — post immediately.
      dispatchMessage(text);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasActiveSession, session],
  );

  // ─── actual round-trip to /ai_chat ─────────────────────────────────────
  //
  // `sessionOverride` lets callers (like the OTP verify handler) pass a
  // freshly-issued session BEFORE React has re-rendered with the new state.
  // Without this, dispatching a queued message right after OTP verification
  // would read a stale `session === null` from the useCallback closure and
  // silently bail — which is exactly the bug that forced users to click
  // send again after verifying.
  const dispatchMessage = useCallback(
    async (text: string, sessionOverride?: StoredAiSession) => {
      const activeSession = sessionOverride ?? session;
      if (!activeSession) return;

      const userMsg: ChatMessage = { id: uid(), role: 'user', text };
      setMessages((prev) => [...prev, userMsg]);
      setInputValue('');
      setIsThinking(true);

      // Include recent chat history (excluding the just-added user msg — it's in `message`).
      const priorHistory: ChatTurn[] = messages
        .filter((m) => !m.error)
        .map(({ role, text: t }) => ({ role, text: t }));

      const res = await sendAiChatMessage({
        session_token: activeSession.session_token,
        work_email: activeSession.work_email,
        message: text,
        history: priorHistory,
      });

      setIsThinking(false);

      if (res.status === 'success' && res.data?.reply) {
        setMessages((prev) => [
          ...prev,
          { id: uid(), role: 'assistant', text: res.data!.reply },
        ]);
        return;
      }

      // Session expired → drop it and prompt re-verification.
      if (res.status_code === 401) {
        clearAiSession();
        setSession(null);
        setPendingUserMessage(text);
        setAuthError('Your verification session expired. Please verify your work email again.');
        setPhase('need-email');
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          id: uid(),
          role: 'assistant',
          text: res.message || 'Sorry, something went wrong. Please try again.',
          error: true,
        },
      ]);
    },
    [messages, session],
  );

  // ─── auth: send OTP ────────────────────────────────────────────────────
  const handleSendOtp = useCallback(async () => {
    setAuthError('');
    if (!authName.trim()) {
      setAuthError('Please enter your name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authEmail.trim())) {
      setAuthError('Please enter a valid work email address.');
      return;
    }
    if (!authPhone.number.trim()) {
      setAuthError('Please enter your phone number.');
      return;
    }
    if (!/^[\d\s-]{6,}$/.test(authPhone.number.trim())) {
      setAuthError('Please enter a valid phone number.');
      return;
    }

    setPhase('sending-otp');
    const res = await startAiChatSession({
      name: authName.trim(),
      work_email: authEmail.trim(),
      phone_number: composePhoneNumber(authPhone),
    });

    if (res.status === 'success') {
      setOtpDigits(['', '', '', '']);
      setOtpError('');
      setOtpResendCooldown(OTP_RESEND_COOLDOWN);
      setPhase('need-otp');
      setTimeout(() => otpInputsRef.current[0]?.focus(), 100);
      return;
    }

    // 400 → not a work email; 429 → cooldown; otherwise generic failure.
    setAuthError(
      res.status_code === 400
        ? res.message ||
            'Please use a work email address. Personal domains (gmail, yahoo, hotmail…) are not accepted.'
        : res.message || 'Could not send verification code. Please try again.',
    );
    setPhase('need-email');
  }, [authName, authEmail, authPhone]);

  // ─── auth: verify OTP ──────────────────────────────────────────────────
  const handleVerifyOtp = useCallback(
    async (fullOtp: string) => {
      setOtpVerifying(true);
      setOtpError('');
      const res = await verifyAiChatSession({
        work_email: authEmail.trim(),
        otp: fullOtp,
        name: authName.trim(),
        phone_number: composePhoneNumber(authPhone),
      });
      setOtpVerifying(false);

      if (res.status === 'success' && res.data?.session_token) {
        const stored: StoredAiSession = {
          session_token: res.data.session_token,
          work_email: res.data.work_email,
          expires_at: res.data.expires_at,
          name: authName.trim(),
        };
        persistAiSession(stored);
        setSession(stored);
        setPhase('chatting');

        // Auto-send the queued question the moment verification succeeds.
        // We pass `stored` explicitly so `dispatchMessage` doesn't read the
        // still-null `session` from its useCallback closure — React hasn't
        // re-rendered yet at this point. No setTimeout needed; Framer Motion
        // handles the concurrent modal-exit / chat-window-enter animation
        // smoothly on its own.
        if (pendingUserMessage) {
          const msg = pendingUserMessage;
          setPendingUserMessage('');
          dispatchMessage(msg, stored);
        }
        return;
      }

      setOtpError(res.message || 'Invalid OTP. Please try again.');
      setOtpDigits(['', '', '', '']);
      setTimeout(() => otpInputsRef.current[0]?.focus(), 50);
    },
    [authEmail, authName, authPhone, pendingUserMessage, dispatchMessage],
  );

  // ─── auth: resend OTP ──────────────────────────────────────────────────
  const handleResendOtp = useCallback(async () => {
    if (otpResendCooldown > 0) return;
    setOtpResending(true);
    setOtpError('');
    const res = await startAiChatSession({
      name: authName.trim(),
      work_email: authEmail.trim(),
      phone_number: composePhoneNumber(authPhone),
    });
    setOtpResending(false);
    if (res.status === 'success') {
      setOtpResendCooldown(OTP_RESEND_COOLDOWN);
      setOtpDigits(['', '', '', '']);
      setTimeout(() => otpInputsRef.current[0]?.focus(), 50);
      return;
    }
    setOtpError(res.message || 'Could not resend the code. Please try again.');
  }, [authName, authEmail, authPhone, otpResendCooldown]);

  // ─── OTP input helpers ─────────────────────────────────────────────────
  const setOtpDigit = useCallback(
    (index: number, value: string) => {
      if (!/^\d*$/.test(value)) return;

      // Handle full-OTP paste.
      if (value.length > 1) {
        const pasted = value.slice(0, 4).split('');
        const next = ['', '', '', ''];
        for (let i = 0; i < 4; i++) next[i] = pasted[i] || '';
        setOtpDigits(next);
        const lastFilled = Math.min(pasted.length, 4) - 1;
        otpInputsRef.current[lastFilled]?.focus();
        const joined = next.join('');
        if (joined.length === 4 && /^\d{4}$/.test(joined)) {
          handleVerifyOtp(joined);
        }
        return;
      }

      const next = [...otpDigits];
      next[index] = value;
      setOtpDigits(next);

      if (value && index < 3) {
        otpInputsRef.current[index + 1]?.focus();
      }
      const joined = next.join('');
      if (joined.length === 4 && /^\d{4}$/.test(joined) && !otpVerifying) {
        handleVerifyOtp(joined);
      }
    },
    [otpDigits, otpVerifying, handleVerifyOtp],
  );

  const closeAuth = () => {
    setPhase(hasActiveSession ? 'chatting' : 'idle');
    setPendingUserMessage('');
    setAuthError('');
    setOtpError('');
  };

  const handleSignOut = () => {
    clearAiSession();
    setSession(null);
    setMessages([]);
    setPhase('idle');
  };

  // ─── form submit ───────────────────────────────────────────────────────
  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    attemptSend(inputValue);
  };

  const handleQuickPick = (pick: QuickPick) => {
    // If already chatting, dispatch immediately; otherwise buffer + gate.
    setInputValue(pick.prompt);
    if (hasActiveSession) {
      dispatchMessage(pick.prompt);
      setInputValue('');
    } else {
      setPendingUserMessage(pick.prompt);
      setAuthError('');
      setOtpError('');
      setPhase('need-email');
    }
  };

  // ─── render ────────────────────────────────────────────────────────────
  return (
    <section
      id="ask-istatec-ai"
      className="relative bg-navy-950 overflow-hidden scroll-mt-24"
    >
      {/* Ambient background */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(1200px 500px at 20% 10%, rgba(26,110,255,0.18) 0%, transparent 60%), radial-gradient(900px 400px at 90% 90%, rgba(107,162,255,0.15) 0%, transparent 60%)',
        }}
      />
      {/* Faint grid */}
      <div aria-hidden className="absolute inset-0 bg-grid-pattern opacity-30" />
      {/* Match the homepage hero's lower edge before revealing the glow. */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-navy-950 to-transparent" />

      <Container className="relative z-10 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: sphere + tagline */}
          <motion.div
            className="lg:col-span-5 text-center lg:text-left"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative inline-block">
              <img
                src={fluidSphere}
                alt=""
                aria-hidden="true"
                className="mx-auto lg:mx-0 w-56 h-56 md:w-72 md:h-72 object-contain drop-shadow-[0_0_60px_rgba(26,110,255,0.35)]"
              />
              <div className="pointer-events-none absolute inset-0 rounded-full blur-3xl bg-accent-500/20" />
            </div>

            <div className="inline-flex items-center gap-2 mt-6 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/90 text-xs font-medium">
              <Sparkles className="w-3.5 h-3.5 text-accent-300" />
              Istatec AI · Powered by Gemini 2.5 Flash
            </div>

            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Ask Istatec AI how to solve your{' '}
              <span className="bg-gradient-to-r from-accent-300 via-accent-400 to-accent-300 bg-clip-text text-transparent">
                business challenge
              </span>
            </h2>
            <p className="mt-4 text-navy-200 text-base md:text-lg max-w-md mx-auto lg:mx-0">
              Describe your problem in plain language. Our on-site AI maps it to the right
              Istatec service, product, or expert team — with a concrete next step.
            </p>

            {hasActiveSession && session && (
              <div className="mt-6 inline-flex items-center gap-3 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-xs">
                <CheckCircle2 className="w-4 h-4" />
                Verified as <span className="font-mono">{session.work_email}</span>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="ml-2 inline-flex items-center gap-1 text-emerald-100 hover:text-white transition-colors"
                  aria-label="Sign out of chat session"
                >
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
            )}
          </motion.div>

          {/* Right: chat card */}
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div
              className={cn(
                'relative rounded-3xl bg-white/95 backdrop-blur-xl shadow-2xl',
                'border border-white/40',
                'overflow-hidden',
              )}
            >
              {/* Top gradient rim */}
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-accent-500 via-accent-400 to-accent-600" />

              {/* Chat window — opens once the user has verified and posted a message */}
              <AnimatePresence initial={false}>
                {messages.length > 0 && (
                  <motion.div
                    key="conversation"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-b border-navy-100"
                  >
                    {/* Chat window header — visible ONLY when the window is open */}
                    <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-3 border-b border-navy-100 bg-navy-50/60">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="relative shrink-0 w-9 h-9 rounded-full overflow-hidden bg-navy-900 ring-1 ring-white/20">
                          <img
                            src={fluidSphere}
                            alt=""
                            aria-hidden="true"
                            className="w-9 h-9 object-cover"
                          />
                          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-navy-900 truncate flex items-center gap-1.5">
                            <MessagesSquare className="w-3.5 h-3.5 text-accent-600" />
                            Istatec AI · Chat
                          </div>
                          {session?.work_email && (
                            <div className="text-[11px] text-navy-500 truncate font-mono">
                              {session.work_email}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="hidden sm:inline text-[11px] uppercase tracking-wider text-emerald-600 font-semibold">
                          Verified
                        </span>
                        <button
                          type="button"
                          onClick={() => setMessages([])}
                          aria-label="Clear chat"
                          title="Clear chat"
                          className="text-xs text-navy-500 hover:text-navy-800 px-2 py-1 rounded hover:bg-white transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div
                      ref={chatScrollRef}
                      className="px-4 sm:px-6 pt-5 pb-4 max-h-[56vh] min-h-[280px] overflow-y-auto space-y-4 bg-gradient-to-b from-white to-navy-50/40"
                    >
                      {messages.map((m) => (
                        <ChatBubble key={m.id} message={m} />
                      ))}
                      {isThinking && <ThinkingBubble />}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Prompt area */}
              <form onSubmit={handleInputSubmit} className="px-4 sm:px-6 py-5">
                <label
                  htmlFor="istatec-ai-prompt"
                  className="block text-sm font-medium text-navy-900 mb-3 text-center"
                >
                  {messages.length === 0
                    ? 'Ask Istatec AI how to solve your business challenge'
                    : 'Continue the conversation'}
                </label>

                <div
                  className={cn(
                    'flex items-center gap-2 rounded-2xl border-2 bg-white px-4 py-2.5 transition-all',
                    'border-navy-200 focus-within:border-accent-500 focus-within:ring-4 focus-within:ring-accent-500/15',
                  )}
                >
                  <div className="shrink-0 w-8 h-8 rounded-full overflow-hidden bg-navy-100 flex items-center justify-center">
                    <img
                      src={fluidSphere}
                      alt=""
                      aria-hidden="true"
                      className="w-8 h-8 object-cover"
                    />
                  </div>
                  <input
                    id="istatec-ai-prompt"
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Please type out your question or tell us about your challenges."
                    className="flex-1 bg-transparent outline-none text-navy-900 placeholder:text-navy-400 py-2"
                    autoComplete="off"
                    disabled={isThinking}
                  />
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isThinking}
                    aria-label="Send message"
                    className={cn(
                      'shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all',
                      inputValue.trim() && !isThinking
                        ? 'bg-navy-900 text-white hover:bg-accent-600'
                        : 'bg-navy-100 text-navy-400 cursor-not-allowed',
                    )}
                  >
                    {isThinking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ArrowUp className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Quick picks */}
                <div className="mt-5">
                  <div className="text-xs font-semibold text-navy-500 uppercase tracking-wider mb-3">
                    Quick picks to get you started
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {QUICK_PICKS.map((pick) => (
                      <QuickPickButton
                        key={pick.title}
                        pick={pick}
                        disabled={isThinking}
                        onClick={handleQuickPick}
                      />
                    ))}
                  </div>
                </div>

                <p className="mt-4 text-center text-xs text-navy-400">
                  We may collect your work email to prevent abuse. By using this chat, you
                  confirm that you agree to Istatec's Privacy Notice.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </Container>

      {/* Auth modal — email → OTP */}
      <AuthGateModal
        phase={phase}
        onClose={closeAuth}
        name={authName}
        email={authEmail}
        phone={authPhone}
        onNameChange={setAuthName}
        onEmailChange={setAuthEmail}
        onPhoneChange={setAuthPhone}
        error={authError}
        onSendOtp={handleSendOtp}
        pendingMessage={pendingUserMessage}
        // otp props
        otpDigits={otpDigits}
        otpInputsRef={otpInputsRef}
        onOtpDigitChange={setOtpDigit}
        otpError={otpError}
        otpVerifying={otpVerifying}
        otpResendCooldown={otpResendCooldown}
        otpResending={otpResending}
        onResendOtp={handleResendOtp}
        onEditEmail={() => {
          setPhase('need-email');
          setOtpError('');
          setOtpDigits(['', '', '', '']);
        }}
      />
    </section>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const QuickPickButton: React.FC<{
  pick: QuickPick;
  disabled?: boolean;
  onClick: (pick: QuickPick) => void;
}> = ({ pick, disabled, onClick }) => {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onClick(pick)}
      className={cn(
        'group relative overflow-hidden text-left rounded-xl px-3.5 py-3',
        'border border-navy-100 bg-navy-50/50 hover:border-accent-300 hover:bg-white',
        'transition-all',
        'disabled:opacity-50 disabled:cursor-not-allowed',
      )}
    >
      <div className="flex items-start gap-2.5">
        <div className="shrink-0 mt-0.5 w-5 h-5 rounded-full overflow-hidden bg-navy-100">
          <img src={fluidSphere} alt="" aria-hidden="true" className="w-5 h-5 object-cover" />
        </div>
        <span className="text-sm text-navy-800 font-medium leading-snug group-hover:text-navy-900">
          {pick.title}
        </span>
      </div>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-accent-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </button>
  );
};

const ChatBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      // Per product spec: user messages are on the LEFT, AI responses on the RIGHT.
      className={cn('flex gap-3', isUser ? 'justify-start' : 'justify-end')}
    >
      {isUser && (
        <div
          aria-hidden
          className="shrink-0 w-8 h-8 rounded-full bg-navy-900 text-white flex items-center justify-center text-xs font-semibold mt-0.5"
        >
          You
        </div>
      )}

      <div
        className={cn(
          'max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed',
          isUser
            // User bubble (left): quiet neutral card
            ? 'bg-white text-navy-900 border border-navy-200 rounded-bl-md whitespace-pre-wrap'
            : message.error
              ? 'bg-red-50 text-red-700 border border-red-200 rounded-br-md whitespace-pre-wrap'
              // AI bubble (right): the star of the show — subtle accent tint
              : 'bg-accent-50 text-navy-900 border border-accent-200 rounded-br-md',
        )}
      >
        {isUser || message.error ? message.text : <AiMarkdown text={message.text} />}
      </div>

      {!isUser && (
        <div className="shrink-0 w-8 h-8 rounded-full overflow-hidden bg-navy-900 ring-1 ring-accent-200 mt-0.5">
          <img src={fluidSphere} alt="" aria-hidden className="w-8 h-8 object-cover" />
        </div>
      )}
    </motion.div>
  );
};

const ThinkingBubble: React.FC = () => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex gap-3 justify-end"
  >
    <div className="px-4 py-3 rounded-2xl bg-accent-50 border border-accent-200 rounded-br-md">
      <div className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-bounce" />
      </div>
    </div>
    <div className="shrink-0 w-8 h-8 rounded-full overflow-hidden bg-navy-900 ring-1 ring-accent-200 mt-0.5">
      <img src={fluidSphere} alt="" aria-hidden className="w-8 h-8 object-cover" />
    </div>
  </motion.div>
);

// ---------------------------------------------------------------------------
// AiMarkdown — a small, safe markdown renderer for Gemini replies.
//
// Supports: paragraphs, headings (# / ## / ###), unordered lists (- * •),
// ordered lists (1. 2. …), fenced code blocks (```lang), inline `code`,
// **bold**, *italic*, [text](url) links. No raw HTML, no dangerouslySetInnerHTML
// — everything renders through React text nodes so we can't inject markup.
// ---------------------------------------------------------------------------

const INLINE_TOKEN_REGEX =
  /(\*\*[^*\n]+\*\*)|(`[^`\n]+`)|(\[[^\]\n]+\]\([^)\s]+\))|(\*[^*\n]+\*)/g;

/**
 * Classify a Markdown-link href into one of three routing kinds:
 *   • `internal-spa` — a site path like `/contact` or `/services/xyz`.
 *                     Must be rendered via react-router's <Link> so we
 *                     don't do a hard page reload (which would drop
 *                     the chat state + the user's verified session).
 *   • `internal-hash` — an in-page anchor like `#ask-istatec-ai`.
 *   • `external`     — an absolute http(s) URL to another site.
 *
 * We also silently coerce `https://istatec.com/<path>` to an SPA link,
 * because that pattern is a common LLM slip-up even after prompting.
 */
type LinkKind = 'internal-spa' | 'internal-hash' | 'external';

const classifyLink = (href: string): { kind: LinkKind; to: string } => {
  const trimmed = href.trim();

  // Absolute istatec.com URL — rewrite to the pathname so we route in
  // the SPA and don't blow away chat/session state on click.
  const istatecOrigin = /^https?:\/\/(?:www\.)?istatec\.com(\/.*)?$/i.exec(trimmed);
  if (istatecOrigin) {
    const path = istatecOrigin[1] || '/';
    return { kind: 'internal-spa', to: path };
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return { kind: 'external', to: trimmed };
  }
  if (trimmed.startsWith('#')) {
    return { kind: 'internal-hash', to: trimmed };
  }
  if (trimmed.startsWith('/')) {
    return { kind: 'internal-spa', to: trimmed };
  }
  // `mailto:`, `tel:`, or anything else we don't recognise — treat as
  // a plain `<a>` (external-style) so it still works.
  return { kind: 'external', to: trimmed };
};

/**
 * Chip-style shared class list for AI-authored inline links. Reads as a
 * mini call-to-action button embedded in the sentence, so a phrase like
 * "head to [Contact us](/contact)" visibly stands out as clickable.
 */
const AI_LINK_CHIP_CLASS =
  'inline-flex items-baseline gap-1 px-2 py-0.5 -my-0.5 rounded-md ' +
  'bg-accent-100/70 hover:bg-accent-200 ' +
  'text-accent-800 hover:text-accent-900 font-semibold no-underline ' +
  'transition-colors align-baseline';

const AiInlineLink: React.FC<{
  href: string;
  children: React.ReactNode;
}> = ({ href, children }) => {
  const { kind, to } = classifyLink(href);

  if (kind === 'internal-spa') {
    // SPA nav — preserve session/chat state.
    return (
      <Link to={to} className={AI_LINK_CHIP_CLASS}>
        {children}
        <ArrowRight className="w-3 h-3 shrink-0 self-center" aria-hidden />
      </Link>
    );
  }

  if (kind === 'internal-hash') {
    return (
      <a href={to} className={AI_LINK_CHIP_CLASS}>
        {children}
        <ArrowRight className="w-3 h-3 shrink-0 self-center" aria-hidden />
      </a>
    );
  }

  return (
    <a
      href={to}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-baseline gap-0.5 text-accent-700 hover:text-accent-800 underline underline-offset-2 decoration-accent-400/60 hover:decoration-accent-600"
    >
      {children}
      <ArrowUpRight className="w-3 h-3 shrink-0 self-center" aria-hidden />
    </a>
  );
};

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  INLINE_TOKEN_REGEX.lastIndex = 0;
  while ((match = INLINE_TOKEN_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const key = `${keyPrefix}-${i++}`;
    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(
        <strong key={key} className="font-semibold text-navy-900">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(
        <code
          key={key}
          className="bg-navy-100 text-navy-800 px-1 py-0.5 rounded font-mono text-[0.85em]"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      if (linkMatch) {
        nodes.push(
          <AiInlineLink key={key} href={linkMatch[2]}>
            {linkMatch[1]}
          </AiInlineLink>,
        );
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith('*') && token.endsWith('*')) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

/**
 * Detect a paragraph that is essentially "just a link" — the pattern the
 * prompt uses for the standalone CTA at the end of a reply, e.g.:
 *
 *     [Let's connect](/contact)
 *
 * When we see this, we render the link as a full CTA button (chunky,
 * arrow icon, hover states) rather than an inline chip — this matches the
 * "AI responds with 'Let's connect' → clickable button" UX the product
 * spec calls out. Also matches a trailing arrow or period the model
 * sometimes adds, e.g. `[Let's connect](/contact) →` or `[…](…).`.
 */
const STANDALONE_LINK_ONLY_REGEX =
  /^\s*\[([^\]\n]+)\]\(([^)\s]+)\)\s*[.→>]?\s*$/;


interface MarkdownBlock {
  kind: 'p' | 'h1' | 'h2' | 'h3' | 'ul' | 'ol' | 'code';
  text: string; // for p/h*
  items?: string[]; // for ul/ol
  language?: string; // for code
}

const parseMarkdown = (raw: string): MarkdownBlock[] => {
  const blocks: MarkdownBlock[] = [];
  const lines = raw.replace(/\r\n/g, '\n').split('\n');

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    const fenceMatch = /^```(\w+)?\s*$/.exec(line.trim());
    if (fenceMatch) {
      const language = fenceMatch[1] || '';
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i].trim())) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // consume the closing fence
      blocks.push({ kind: 'code', text: codeLines.join('\n'), language });
      continue;
    }

    // Blank line → paragraph break
    if (!line.trim()) {
      i++;
      continue;
    }

    // Headings
    if (/^###\s+/.test(line)) {
      blocks.push({ kind: 'h3', text: line.replace(/^###\s+/, '') });
      i++;
      continue;
    }
    if (/^##\s+/.test(line)) {
      blocks.push({ kind: 'h2', text: line.replace(/^##\s+/, '') });
      i++;
      continue;
    }
    if (/^#\s+/.test(line)) {
      blocks.push({ kind: 'h1', text: line.replace(/^#\s+/, '') });
      i++;
      continue;
    }

    // Unordered list
    if (/^\s*[-*•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*•]\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ul', text: '', items });
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ol', text: '', items });
      continue;
    }

    // Paragraph — greedy until a blank line or a new-block prefix
    const paragraphLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^```/.test(lines[i].trim()) &&
      !/^#{1,3}\s+/.test(lines[i]) &&
      !/^\s*[-*•]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i])
    ) {
      paragraphLines.push(lines[i]);
      i++;
    }
    blocks.push({ kind: 'p', text: paragraphLines.join('\n') });
  }

  return blocks;
};

const AiMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const blocks = useMemo(() => parseMarkdown(text), [text]);

  return (
    <div className="space-y-2 text-sm leading-relaxed text-navy-900">
      {blocks.map((block, idx) => {
        const key = `blk-${idx}`;
        switch (block.kind) {
          case 'h1':
            return (
              <h4 key={key} className="text-base font-bold text-navy-900 mt-1">
                {renderInline(block.text, key)}
              </h4>
            );
          case 'h2':
            return (
              <h5 key={key} className="text-[15px] font-bold text-navy-900 mt-1">
                {renderInline(block.text, key)}
              </h5>
            );
          case 'h3':
            return (
              <h6 key={key} className="text-sm font-semibold text-navy-900 mt-1">
                {renderInline(block.text, key)}
              </h6>
            );
          case 'ul':
            return (
              <ul key={key} className="list-disc pl-5 space-y-1 marker:text-accent-500">
                {block.items!.map((item, j) => (
                  <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="list-decimal pl-5 space-y-1 marker:text-accent-500 marker:font-semibold">
                {block.items!.map((item, j) => (
                  <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
                ))}
              </ol>
            );
          case 'code':
            return (
              <pre
                key={key}
                className="bg-navy-900 text-navy-100 rounded-lg p-3 overflow-x-auto text-[12px] leading-snug font-mono"
              >
                <code>{block.text}</code>
              </pre>
            );
          case 'p':
          default: {
            // Standalone-link paragraph → render as a big CTA button.
            // The system prompt asks the model to close every reply with
            // exactly this pattern (e.g. "[Let's connect](/contact)" on
            // its own line), so we upgrade it visually from an inline
            // chip to a proper button.
            const standalone = STANDALONE_LINK_ONLY_REGEX.exec(block.text);
            if (standalone) {
              const label = standalone[1];
              const href = standalone[2];
              return <AiCtaButton key={key} label={label} href={href} />;
            }
            return (
              <p key={key} className="whitespace-pre-wrap">
                {renderInline(block.text, key)}
              </p>
            );
          }
        }
      })}
    </div>
  );
};

/**
 * Full-width call-to-action button used for standalone-link paragraphs
 * emitted by the AI (see `STANDALONE_LINK_ONLY_REGEX`). Renders as a
 * navy pill with a right-arrow — the same visual grammar as the
 * primary Button used across the site — so the "Let's connect" line
 * feels like a proper button, not another underline.
 *
 * Routes via react-router when the href is an internal path, so we
 * preserve chat state on click. External URLs open in a new tab.
 */
const AiCtaButton: React.FC<{ label: string; href: string }> = ({ label, href }) => {
  const { kind, to } = classifyLink(href);

  const buttonClass =
    'group inline-flex items-center gap-2 mt-1 px-4 py-2 rounded-full ' +
    'bg-navy-900 hover:bg-accent-600 text-white text-sm font-semibold ' +
    'shadow-sm transition-colors no-underline';

  if (kind === 'external') {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={buttonClass}>
        {label}
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </a>
    );
  }
  if (kind === 'internal-hash') {
    return (
      <a href={to} className={buttonClass}>
        {label}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </a>
    );
  }
  return (
    <Link to={to} className={buttonClass}>
      {label}
      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
    </Link>
  );
};

// ---------------------------------------------------------------------------
// Auth gate modal (email → OTP). Kept inline so the AskIstatecAI section is a
// single self-contained widget.
// ---------------------------------------------------------------------------

interface AuthGateModalProps {
  phase: Phase;
  onClose: () => void;
  name: string;
  email: string;
  phone: PhoneValue;
  onNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onPhoneChange: (v: PhoneValue) => void;
  error: string;
  onSendOtp: () => void;
  pendingMessage: string;

  otpDigits: string[];
  otpInputsRef: React.MutableRefObject<(HTMLInputElement | null)[]>;
  onOtpDigitChange: (index: number, value: string) => void;
  otpError: string;
  otpVerifying: boolean;
  otpResendCooldown: number;
  otpResending: boolean;
  onResendOtp: () => void;
  onEditEmail: () => void;
}

const AuthGateModal: React.FC<AuthGateModalProps> = ({
  phase,
  onClose,
  name,
  email,
  phone,
  onNameChange,
  onEmailChange,
  onPhoneChange,
  error,
  onSendOtp,
  pendingMessage,
  otpDigits,
  otpInputsRef,
  onOtpDigitChange,
  otpError,
  otpVerifying,
  otpResendCooldown,
  otpResending,
  onResendOtp,
  onEditEmail,
}) => {
  const isOpen = phase === 'need-email' || phase === 'sending-otp' || phase === 'need-otp';

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-navy-950/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg text-navy-400 hover:text-navy-700 hover:bg-navy-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="bg-gradient-to-br from-navy-900 to-navy-800 px-8 pt-8 pb-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-500/20 flex items-center justify-center">
                {phase === 'need-otp' ? (
                  <ShieldCheck className="w-8 h-8 text-accent-300" />
                ) : (
                  <Mail className="w-8 h-8 text-accent-300" />
                )}
              </div>
              <h3 className="text-xl font-bold text-white mb-1">
                {phase === 'need-otp' ? 'Verify Your Work Email' : 'Verify to Chat with Istatec AI'}
              </h3>
              <p className="text-navy-300 text-sm">
                {phase === 'need-otp' ? (
                  <>
                    We've sent a 4-digit code to
                    <br />
                    <span className="text-white font-medium">{email}</span>
                  </>
                ) : (
                  'Enter your name and work email to start the conversation.'
                )}
              </p>
            </div>

            {/* Body */}
            <div className="px-8 py-6">
              {pendingMessage && (
                <div className="mb-4 p-3 rounded-lg bg-accent-50 border border-accent-200">
                  <p className="text-xs font-semibold text-accent-700 mb-1">
                    Your question is queued
                  </p>
                  <p className="text-sm text-navy-700 line-clamp-3">"{pendingMessage}"</p>
                </div>
              )}

              {(phase === 'need-email' || phase === 'sending-otp') && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (phase !== 'sending-otp') onSendOtp();
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="ai-auth-name" className="block text-sm font-medium text-navy-700 mb-1.5">
                      Full Name
                    </label>
                    <input
                      id="ai-auth-name"
                      type="text"
                      value={name}
                      onChange={(e) => onNameChange(e.target.value)}
                      autoFocus
                      className="w-full px-4 py-2.5 rounded-lg border border-navy-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all bg-white text-navy-900"
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="ai-auth-email" className="block text-sm font-medium text-navy-700 mb-1.5">
                      Work Email
                    </label>
                    <input
                      id="ai-auth-email"
                      type="email"
                      value={email}
                      onChange={(e) => onEmailChange(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border border-navy-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all bg-white text-navy-900"
                      placeholder="you@company.com"
                    />
                    <p className="mt-1 text-xs text-navy-400">
                      Personal domains (gmail, yahoo, hotmail…) are not accepted.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="ai-auth-phone" className="block text-sm font-medium text-navy-700 mb-1.5">
                      Phone Number
                    </label>
                    <PhoneInput
                      id="ai-auth-phone"
                      value={phone}
                      onChange={onPhoneChange}
                    />
                    <p className="mt-1 text-xs text-navy-400">
                      Our team may follow up here after your chat.
                    </p>
                  </div>

                  {error && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    className="w-full"
                    isLoading={phase === 'sending-otp'}
                    rightIcon={<Send className="w-4 h-4" />}
                  >
                    Send verification code
                  </Button>

                  <p className="text-xs text-navy-400 text-center">
                    We'll send a 4-digit code to your work email — only to prevent abuse.
                  </p>
                </form>
              )}

              {phase === 'need-otp' && (
                <>
                  <div className="flex justify-center gap-3 mb-4">
                    {otpDigits.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => {
                          otpInputsRef.current[i] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={i === 0 ? 4 : 1}
                        value={digit}
                        onChange={(e) => onOtpDigitChange(i, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !otpDigits[i] && i > 0) {
                            otpInputsRef.current[i - 1]?.focus();
                          }
                        }}
                        disabled={otpVerifying}
                        className={cn(
                          'w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all',
                          otpVerifying
                            ? 'bg-navy-50 border-navy-200 text-navy-400'
                            : 'bg-white border-navy-200 text-navy-900 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20',
                          otpError && !otpVerifying ? 'border-red-400 bg-red-50' : '',
                        )}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>

                  {otpVerifying && (
                    <div className="flex items-center justify-center gap-2 mb-3 text-accent-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-medium">Verifying…</span>
                    </div>
                  )}

                  {otpError && !otpVerifying && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 mb-3">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                      <p className="text-sm text-red-600">{otpError}</p>
                    </div>
                  )}

                  <div className="text-center">
                    <p className="text-sm text-navy-500 mb-2">Didn't receive the code?</p>
                    {otpResendCooldown > 0 ? (
                      <p className="text-sm text-navy-400">
                        Resend available in{' '}
                        <span className="font-mono font-medium text-navy-600">
                          {formatTime(otpResendCooldown)}
                        </span>
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={onResendOtp}
                        disabled={otpResending}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-600 hover:text-accent-700 disabled:opacity-50"
                      >
                        {otpResending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Resend OTP
                      </button>
                    )}
                  </div>

                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={onEditEmail}
                      className="text-xs text-navy-400 hover:text-navy-600 underline underline-offset-2"
                    >
                      Use a different email
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
